import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, Loader2, Image as ImageIcon } from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { apiGet } from '../lib/api'

interface BannerForm {
  title: string
  subtitle: string
  image: string
  link_url: string
  position: 'hero' | 'top' | 'side' | 'bottom'
  sort_order: number
  is_active: boolean
  valid_from: string
  valid_until: string
}

const empty: BannerForm = {
  title: '',
  subtitle: '',
  image: '',
  link_url: '',
  position: 'top',
  sort_order: 0,
  is_active: true,
  valid_from: '',
  valid_until: '',
}

const POSITION_OPTIONS = [
  { value: 'hero', label: 'Hero (banner principal)' },
  { value: 'top', label: 'Superior (sobre el hero)' },
  { value: 'side', label: 'Lateral' },
  { value: 'bottom', label: 'Inferior (antes del footer)' },
]

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  // Formato yyyy-mm-ddTHH:MM aceptado por <input type="datetime-local">
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

interface Props {
  mode: 'create' | 'edit'
}

export function BannerFormPage({ mode }: Props) {
  const params = mode === 'edit' ? useParams({ strict: false }) : { id: '' }
  const id = (params as any).id as string
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState<BannerForm>(empty)
  const [err, setErr] = useState<string | null>(null)

  const { data, isLoading } = useQuery<any>({
    queryKey: ['admin-banner', id],
    queryFn: () => apiGet(`/api/admin/banners/${id}`),
    enabled: mode === 'edit',
  })

  useEffect(() => {
    if (mode === 'edit' && data) {
      setForm({
        title: data.title ?? '',
        subtitle: data.subtitle ?? '',
        image: data.image ?? '',
        link_url: data.linkUrl ?? '',
        position: data.position ?? 'top',
        sort_order: data.sortOrder ?? 0,
        is_active: !!data.isActive,
        valid_from: toDateInput(data.validFrom),
        valid_until: toDateInput(data.validUntil),
      })
    }
  }, [data, mode])

  const save = useMutation({
    mutationFn: async (p: BannerForm) => {
      const url = mode === 'edit' ? `/api/admin/banners/${id}` : `/api/admin/banners`
      const method = mode === 'edit' ? 'PUT' : 'POST'
      const body = {
        title: p.title,
        subtitle: p.subtitle,
        image: p.image,
        link_url: p.link_url,
        position: p.position,
        sort_order: Number(p.sort_order) || 0,
        is_active: p.is_active,
        valid_from: p.valid_from ? new Date(p.valid_from).toISOString() : null,
        valid_until: p.valid_until ? new Date(p.valid_until).toISOString() : null,
      }
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-banners'] })
      qc.invalidateQueries({ queryKey: ['admin-banner', id] })
      navigate({ to: '/banners' })
    },
    onError: (e: Error) => setErr(e.message),
  })

  function set<K extends keyof BannerForm>(k: K, v: BannerForm[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    save.mutate(form)
  }

  const title = mode === 'create' ? 'Nuevo banner' : 'Editar banner'

  return (
    <>
      <Topbar
        title={title}
        actions={
          <Link
            to="/banners"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Cancelar
          </Link>
        }
      />

      <div className="p-6">
        {mode === 'edit' && isLoading ? (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mx-auto max-w-5xl space-y-6">
            {err && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {err}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Columna principal */}
              <div className="space-y-4 lg:col-span-2">
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <div className="grid grid-cols-1 gap-4">
                    <Field label="Título">
                      <input
                        value={form.title}
                        onChange={(e) => set('title', e.target.value)}
                        maxLength={180}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-base font-semibold outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="Subtítulo">
                      <textarea
                        rows={2}
                        value={form.subtitle}
                        onChange={(e) => set('subtitle', e.target.value)}
                        maxLength={300}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="URL de la imagen">
                      <input
                        value={form.image}
                        onChange={(e) => set('image', e.target.value)}
                        placeholder="/img/banners/…"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="Link al hacer click (opcional)">
                      <input
                        value={form.link_url}
                        onChange={(e) => set('link_url', e.target.value)}
                        placeholder="/categoria/frenos"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                      />
                    </Field>
                  </div>
                </div>

                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h3 className="mb-3 text-sm font-semibold text-ink-900">Preview</h3>
                  <div className="relative aspect-[4/1] overflow-hidden rounded-xl border border-ink-200 bg-gradient-to-br from-ink-100 to-ink-200">
                    {form.image ? (
                      <img
                        src={form.image}
                        alt={form.title}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="grid h-full place-items-center text-ink-400">
                        <div className="text-center">
                          <ImageIcon className="mx-auto size-8" />
                          <p className="mt-2 text-xs">Pega la URL para previsualizar</p>
                        </div>
                      </div>
                    )}
                    {(form.title || form.subtitle) && (
                      <div className="absolute inset-0 flex flex-col justify-center bg-gradient-to-r from-black/60 via-black/30 to-transparent p-6 text-white">
                        {form.title && (
                          <div className="text-lg font-extrabold sm:text-2xl">{form.title}</div>
                        )}
                        {form.subtitle && (
                          <div className="text-xs sm:text-sm">{form.subtitle}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Columna lateral */}
              <aside className="space-y-4">
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h3 className="mb-3 text-sm font-semibold text-ink-900">Posición y orden</h3>
                  <div className="space-y-3">
                    <Field label="Posición">
                      <select
                        value={form.position}
                        onChange={(e) =>
                          set('position', e.target.value as BannerForm['position'])
                        }
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      >
                        {POSITION_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Orden">
                      <input
                        type="number"
                        value={form.sort_order}
                        onChange={(e) => set('sort_order', Number(e.target.value))}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                  </div>
                </div>

                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h3 className="mb-3 text-sm font-semibold text-ink-900">Vigencia</h3>
                  <div className="space-y-3">
                    <Field label="Válido desde">
                      <input
                        type="datetime-local"
                        value={form.valid_from}
                        onChange={(e) => set('valid_from', e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="Válido hasta">
                      <input
                        type="datetime-local"
                        value={form.valid_until}
                        onChange={(e) => set('valid_until', e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <p className="text-xs text-ink-500">Deja vacío para no limitar.</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <label className="flex cursor-pointer items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-ink-900">Activo</div>
                      <p className="text-xs text-ink-500">Visible en el sitio público.</p>
                    </div>
                    <span className="relative inline-block h-6 w-11">
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(e) => set('is_active', e.target.checked)}
                        className="peer sr-only"
                      />
                      <span className="absolute inset-0 rounded-full bg-ink-200 transition-colors peer-checked:bg-brand-600" />
                      <span className="absolute left-0.5 top-0.5 size-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={save.isPending}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {save.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                  Guardar
                </button>
              </aside>
            </div>
          </form>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      {children}
    </div>
  )
}

export default BannerFormPage
